import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Share, MoreVertical, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function InstallGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50 p-4">
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">
            Install Taptile App
          </h1>
          <p className="text-muted-foreground">
            Add to your home screen for easy access
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <img src="https://cdn-icons-png.flaticon.com/512/0/747.png" alt="iPhone" className="w-6 h-6" />
              iPhone / iPad (Safari)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <div>
                  <p className="font-medium">Open Safari browser</p>
                  <p className="text-sm text-muted-foreground">Navigate to your Taptile portal URL</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <div>
                  <p className="font-medium">Tap the Share button</p>
                  <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded">
                    <Share className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">At the bottom of your screen</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <div>
                  <p className="font-medium">Tap "Add to Home Screen"</p>
                  <p className="text-sm text-muted-foreground mt-1">Scroll down in the share menu to find this option</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  4
                </div>
                <div>
                  <p className="font-medium">Tap "Add"</p>
                  <p className="text-sm text-muted-foreground mt-1">The app icon will appear on your home screen</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <img src="https://cdn-icons-png.flaticon.com/512/0/732.png" alt="Android" className="w-6 h-6" />
              Android (Chrome)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <div>
                  <p className="font-medium">Open Chrome browser</p>
                  <p className="text-sm text-muted-foreground">Navigate to your Taptile portal URL</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <div>
                  <p className="font-medium">Tap the menu (three dots)</p>
                  <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded">
                    <MoreVertical className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">In the top-right corner</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <div>
                  <p className="font-medium">Tap "Install app" or "Add to Home screen"</p>
                  <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 rounded">
                    <Download className="w-5 h-5 text-green-600" />
                    <span className="text-sm">You may see an install banner automatically</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  4
                </div>
                <div>
                  <p className="font-medium">Tap "Install"</p>
                  <p className="text-sm text-muted-foreground mt-1">The app will be added to your home screen and app drawer</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-base">Benefits of Installing</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Quick access from your home screen</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Full-screen experience without browser bars</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Faster loading and better performance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Works offline (limited functionality)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Receive push notifications</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Button onClick={() => navigate('/')} className="w-full">
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
